/**
 * L4: copy, then clear on the next tap (01-references §3, 03 CSO caveat).
 *
 * WebKit accepts `navigator.clipboard.writeText` only inside a user gesture, and the page is
 * suspended while the operator pastes in another app. So a timed clear (1Password 90 s, the Mac
 * panel 45 s) cannot run. What a PWA can do:
 * - keep the clipboard state visible for as long as a copied value may still be there;
 * - clear it on the next tap anywhere in the app, which IS a gesture;
 * - never claim it cleared when the write failed.
 * One limit remains: the clear cannot check whether another app wrote the clipboard since
 * (reading it shows a Paste prompt), so it clears unconditionally. The UI says so.
 */
import type { ClipboardPort, Clock } from "../contract.js";

export interface ClipboardState {
  label: string;
  since: number;
}

/**
 * True when a tap landed on, or inside, a copy control: an element marked `data-copy`. The
 * shell's tap guard skips these, so the control's own copy is the tap's only clipboard write.
 * Every control that copies must carry the marker (tests/vault-pwa/copy-controls.test.ts).
 */
export function isCopyControl(target: { closest?: (selector: string) => unknown } | null): boolean {
  return !!target?.closest?.("[data-copy]");
}

export class ClipboardGuard {
  state: ClipboardState | null = null;

  constructor(
    private readonly port: ClipboardPort,
    private readonly clock: Clock,
    private readonly onChange: (s: ClipboardState | null) => void = () => {},
  ) {}

  /** Call from the copy tap's own handler. */
  async copy(value: string, label: string): Promise<boolean> {
    try {
      await this.port.writeText(value);
    } catch {
      return false;
    }
    this.set({ label, since: this.clock.now() });
    return true;
  }

  /** Clears the clipboard. Must run inside a tap. Returns false and keeps the state if refused. */
  async clear(): Promise<boolean> {
    try {
      await this.port.writeText("");
    } catch {
      return false;
    }
    this.set(null);
    return true;
  }

  /**
   * Every tap in the app lands here first (capture phase). A tap on a copy control is left alone:
   * that control's own copy overwrites the clipboard, so clearing first would be wasted work.
   */
  async onTap(target: { isCopyControl: boolean }): Promise<void> {
    if (this.state && !target.isCopyControl) await this.clear();
  }

  private set(s: ClipboardState | null): void {
    this.state = s;
    this.onChange(s);
  }
}
